import {
  IconBuilding,
  IconStethoscope,
  IconAlertTriangle,
  IconAddressBook,
  IconMail,
  IconCreditCard,
  IconMicroscope,
  IconFileText,
  IconShieldCheck,
  IconActivity,
} from "@tabler/icons-react";
import { isAiFeedbackEnabled } from "../utils/aiFeedbackFlag";
import type { UserProfile } from "../store/auth.store";

export interface AdminMenuItem {
  label: string;
  path: string;
  /** Permission required to see this item. Omit to show to all admin users. */
  permission?: string;
}

export function canViewAdminMenuItem(
  item: AdminMenuItem,
  profile: UserProfile | null
): boolean {
  return (
    !item.permission || Boolean(profile?.permissions?.includes(item.permission))
  );
}

export interface AdminSection {
  id: string;
  label: string;
  description: string;
  icon: typeof IconBuilding;
  color: string;
  items: AdminMenuItem[];
}

export const adminSections: AdminSection[] = [
  {
    id: "legacy-cases",
    label: "Legacy Cases",
    description: "Review and import archived NovoPath cases",
    icon: IconFileText,
    color: "blue",
    items: [
      {
        label: "Legacy Cases",
        path: "/admin/legacy-cases",
        permission: "legacy_cases.manage",
      },
    ],
  },
  {
    id: "organization",
    label: "Organization & Access Control",
    description: "Manage facilities, users, roles, and permissions",
    icon: IconBuilding,
    color: "blue",
    items: [
      {
        label: "External Institutions",
        path: "/admin/external-institutions",
        permission: "send_outs.manage",
      },
      {
        label: "Facilities",
        path: "/admin/facilities",
        permission: "facilities.manage",
      },
      {
        label: "Group Tasks",
        path: "/admin/group-tasks",
        permission: "groups.manage",
      },
      { label: "Groups", path: "/admin/groups", permission: "groups.manage" },
      {
        label: "Permissions",
        path: "/admin/permissions",
        permission: "permissions.read",
      },
      {
        label: "Role Tasks",
        path: "/admin/role-tasks",
        permission: "roles.manage",
      },
      { label: "Roles", path: "/admin/roles", permission: "roles.manage" },
      { label: "Tasks", path: "/admin/tasks", permission: "admin.access" },
      { label: "Users", path: "/admin/users", permission: "users.manage" },
    ],
  },
  {
    id: "medical-reference",
    label: "Medical Reference Data",
    description: "Manage procedures, sources, stains, codes, and diagnoses",
    icon: IconStethoscope,
    color: "teal",
    items: [
      {
        label: "Abbreviations",
        path: "/admin/abbreviations",
        permission: "abbreviations.manage",
      },
      {
        label: "Addendum Macros",
        path: "/admin/addendum-macros",
        permission: "reference_data.manage",
      },
      {
        label: "Accessioning Gross Codes",
        path: "/admin/accessioning-gross-codes",
        permission: "reference_data.manage",
      },
      {
        label: "Case Flags",
        path: "/admin/case-flags",
        permission: "flags.manage",
      },
      {
        label: "Case Statuses",
        path: "/admin/case-statuses",
      },
      {
        label: "Collection Methods",
        path: "/admin/collection-methods",
        permission: "reference_data.manage",
      },
      {
        label: "CPT Codes",
        path: "/admin/cpt-codes",
        permission: "billing.manage",
      },
      {
        label: "CPT Modifiers",
        path: "/admin/cpt-modifiers",
        permission: "billing.manage",
      },
      {
        label: "Diagnosis Categories",
        path: "/admin/diagnosis-categories",
        permission: "reference_data.manage",
      },
      {
        label: "Diagnosis Macros",
        path: "/admin/diagnosis",
        permission: "reference_data.manage",
      },
      {
        label: "ICD Codes",
        path: "/admin/icd-codes",
        permission: "billing.manage",
      },
      {
        label: "Internal Gross Codes",
        path: "/admin/internal-gross-codes",
        permission: "reference_data.manage",
      },
      {
        label: "Panels",
        path: "/admin/panels",
        permission: "panels.manage",
      },
      {
        label: "Reflexes",
        path: "/admin/reflexes",
        permission: "panels.manage",
      },
      {
        label: "Procedures",
        path: "/admin/procedures",
        permission: "reference_data.manage",
      },
      {
        label: "Source Default Stains",
        path: "/admin/source-default-stains",
        permission: "reference_data.manage",
      },
      {
        label: "Source Subsites",
        path: "/admin/source-subsites",
        permission: "reference_data.manage",
      },
      {
        label: "Sources",
        path: "/admin/sources",
        permission: "reference_data.manage",
      },
      {
        label: "Stain Providers",
        path: "/admin/stain-providers",
        permission: "reference_data.manage",
      },
      {
        label: "Stains",
        path: "/admin/stains",
        permission: "reference_data.manage",
      },
      {
        label: "Specimen Macros",
        path: "/admin/specimen-macros",
        permission: "reference_data.manage",
      },
      {
        label: "Workflows",
        path: "/admin/workflows",
      },
    ],
  },
  {
    id: "quality-control",
    label: "Quality Control",
    description: "Manage errors and corrections",
    icon: IconAlertTriangle,
    color: "orange",
    items: [
      {
        label: "Corrections",
        path: "/admin/corrections",
        permission: "corrections.manage",
      },
      { label: "Errors", path: "/admin/errors", permission: "errors.read" },
    ],
  },
  {
    id: "practices",
    label: "Practices & Physicians",
    description:
      "Manage practices, physicians, surgery centers, and sales reps",
    icon: IconAddressBook,
    color: "grape",
    items: [
      {
        label: "Practices",
        path: "/admin/practices",
        permission: "practices.manage",
      },
      {
        label: "Physicians",
        path: "/admin/physicians",
        permission: "physicians.manage",
      },
      {
        label: "Surgery Centers / Labs",
        path: "/admin/surgery-centers-labs",
        permission: "practices.manage",
      },
      {
        label: "Sales Reps",
        path: "/admin/sales-reps",
        permission: "practices.manage",
      },
      {
        label: "Portal Access",
        path: "/admin/portal-access",
        permission: "portal_access.read",
      },
    ],
  },
  {
    id: "report-delivery",
    label: "Report Delivery",
    description: "Manage report delivery methods and preferences",
    icon: IconMail,
    color: "indigo",
    items: [
      {
        label: "Practice Report Delivery",
        path: "/admin/account-report-delivery",
        permission: "report_delivery.manage",
      },
      {
        label: "Physician Report Delivery",
        path: "/admin/physician-report-delivery",
        permission: "report_delivery.manage",
      },
      {
        label: "Surgery Center Report Delivery",
        path: "/admin/surgery-center-report-delivery",
        permission: "report_delivery.manage",
      },
      {
        label: "Report Delivery Methods",
        path: "/admin/report-delivery-methods",
        permission: "report_delivery.manage",
      },
    ],
  },
  {
    id: "billing",
    label: "Billing",
    description: "Manage billing scenarios and rules",
    icon: IconCreditCard,
    color: "green",
    items: [
      {
        label: "Billing Scenarios",
        path: "/admin/billing-scenarios",
        permission: "billing.manage",
      },
      {
        label: "Global Insurance Defaults",
        path: "/admin/billing/global-insurance",
        permission: "prefix.admin",
      },
      {
        label: "Prefix Configuration",
        path: "/admin/billing/prefix-configuration",
        permission: "prefix.admin",
      },
      {
        label: "Insurance Plan Types",
        path: "/admin/insurance-plan-types",
        permission: "insurance.manage",
      },
      {
        label: "Insurance Providers",
        path: "/admin/insurance-providers",
        permission: "insurance.manage",
      },
      {
        label: "Provider Plan Types",
        path: "/admin/insurance-provider-plan-types",
        permission: "insurance.manage",
      },
      {
        label: "Lab Payment Agreements",
        path: "/admin/lab-payment-agreements",
        permission: "reports.payables.manage",
      },
    ],
  },
  {
    id: "report-configuration",
    label: "Report Configuration",
    description: "Manage report templates and document layouts",
    icon: IconFileText,
    color: "violet",
    items: [
      {
        label: "Organ Map Templates",
        path: "/admin/organ-map-templates",
        permission: "report_templates.manage",
      },
      {
        label: "Report Templates",
        path: "/admin/report-templates",
        permission: "report_templates.manage",
      },
      {
        label: "Report Review Requirements",
        path: "/admin/report-review-settings",
        permission: "report_review_settings.manage",
      },
    ],
  },
  {
    id: "compliance",
    label: "Compliance & Audit",
    description: "HIPAA audit trail and compliance monitoring",
    icon: IconShieldCheck,
    color: "red",
    items: [
      {
        label: "Audit Logs",
        path: "/admin/audit-logs",
        permission: "audit_logs.read",
      },
      {
        label: "Communication Logs",
        path: "/admin/communication-logs",
        permission: "communication_logs.read",
      },
      {
        label: "System Emails",
        path: "/admin/system-emails",
        permission: "system_emails.read",
      },
    ],
  },
  {
    id: "equipment",
    label: "Equipment & Vendors",
    description: "Manage machines, equipment categories, and vendors",
    icon: IconMicroscope,
    color: "cyan",
    items: [
      {
        label: "Machine Categories",
        path: "/admin/machine-categories",
        permission: "machines.manage",
      },
      {
        label: "Machines",
        path: "/admin/machines",
        permission: "machines.manage",
      },
      {
        label: "Vendors",
        path: "/admin/vendors",
        permission: "machines.manage",
      },
      { label: "Device Management", path: "/admin/bridge" },
      {
        label: "Slide Printing",
        path: "/admin/slide-print-settings",
        permission: "slide_print_settings.manage",
      },
      {
        label: "Print & Scan Audit Log",
        path: "/admin/bridge-audit",
        permission: "bridge.audit.read",
      },
    ],
  },
  {
    id: "system-diagnostics",
    label: "System Diagnostics",
    description: "View session recordings and reported observations",
    icon: IconActivity,
    color: "red",
    items: [
      ...(isAiFeedbackEnabled()
        ? [
            {
              label: "AI Extraction",
              path: "/admin/ai-extraction",
              permission: "ai_extraction.view_report",
            },
          ]
        : []),
      {
        label: "Session Recordings",
        path: "/admin/session-recordings",
        permission: "session_recordings.view",
      },
      {
        label: "Observations",
        path: "/admin/observations",
        permission: "errors.resolve",
      },
    ],
  },
];
